/**
 * Global keyboard-shortcut layer + `?` cheat-sheet overlay.
 *
 * A single window-level listener (mounted once in the shell) that adds a
 * discoverable shortcut surface on top of the existing Cmd/Ctrl-K palette:
 *
 *   ?            open this cheat-sheet
 *   /            open the command palette (global search)
 *   g then <key> "go to" navigation (g d = dashboard, g c = contenu, …)
 *
 * Guards: never fires while the user is typing in an input/textarea/select or
 * a contenteditable, never with a modifier held (so Cmd/Ctrl-K etc. keep
 * working), and never while a modal/palette is open (`[aria-modal="true"]`).
 * `g`-chords expire after 1.2 s.
 *
 * The overlay reuses the shared `@/ui` `Modal` (focus-trap, scroll-lock,
 * Escape/backdrop close, bottom-sheet on mobile), so it is theme-aware and
 * accessible for free.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { Modal } from '@/ui';
import { useT, tr } from '@/i18n';

// ---------------------------------------------------------------------------
// Focus-trap primitives (shared by the mobile nav drawer and the command
// palette — both need Tab to cycle inside a transient surface).
//
// Kept here (not in DashboardLayout) so the command palette can import them
// without creating a DashboardLayout ↔ CommandPalette import cycle, and so the
// pure `nextTrapFocus` logic is unit-testable without the shell's import graph.
// ---------------------------------------------------------------------------

/** Elements that can receive keyboard focus inside a trapped container. */
export const FOCUSABLE_SELECTOR =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

/** All tabbable descendants of `container`, in DOM order. */
export function getFocusable(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

/**
 * Pure focus-trap decision. Given the tabbable `items`, the currently focused
 * element and whether Shift is held, return the element that should receive
 * focus to keep the trap cyclic — or `null` when the browser's default Tab is
 * fine (focus is mid-list and staying inside).
 *
 *   - Tab on the last item (or focus outside the trap) → wrap to the first.
 *   - Shift+Tab on the first item (or focus outside) → wrap to the last.
 */
export function nextTrapFocus(
  items: HTMLElement[],
  active: Element | null,
  shift: boolean,
): HTMLElement | null {
  if (items.length === 0) return null;
  const first = items[0];
  const last = items[items.length - 1];
  const inside = active instanceof HTMLElement && items.includes(active);
  if (shift) {
    if (active === first || !inside) return last;
  } else if (active === last || !inside) {
    return first;
  }
  return null;
}

/** "Go to" chords: the key pressed after `g`, and where it navigates. */
export const GOTO_NAV: { key: string; to: string; label: string }[] = [
  { key: 'd', to: '/', label: tr('shell.app.keyboardShortcuts.dashboard') },
  { key: 'c', to: '/content', label: tr('shell.app.keyboardShortcuts.content') },
  { key: 'k', to: '/calendar', label: tr('shell.app.keyboardShortcuts.calendar') },
  { key: 'p', to: '/publishing', label: tr('shell.app.keyboardShortcuts.publishing') },
  { key: 'l', to: '/platforms', label: tr('shell.app.keyboardShortcuts.platforms') },
  { key: 'm', to: '/media-library', label: tr('shell.app.keyboardShortcuts.mediaLibrary') },
  { key: 'a', to: '/analytics', label: tr('shell.app.keyboardShortcuts.analytics') },
  { key: 'w', to: '/workflows', label: tr('shell.app.keyboardShortcuts.workflows') },
  { key: 'r', to: '/crm', label: tr('shell.app.keyboardShortcuts.crmLeads') },
  { key: 'n', to: '/social', label: tr('shell.app.keyboardShortcuts.socialLeads') },
  { key: 'o', to: '/outreach', label: tr('shell.app.keyboardShortcuts.emailOutreach') },
  { key: 'i', to: '/inbox', label: tr('shell.app.keyboardShortcuts.socialInbox') },
  { key: 'e', to: '/listening', label: tr('shell.app.keyboardShortcuts.socialListening') },
  { key: 'b', to: '/bio', label: 'Link-in-bio' },
  { key: 'j', to: '/projects', label: tr('shell.app.keyboardShortcuts.projects') },
  { key: 'x', to: '/ai-activity', label: tr('shell.app.keyboardShortcuts.aiActivity') },
  { key: 's', to: '/settings', label: tr('shell.app.keyboardShortcuts.parameters') },
];

const GOTO_BY_KEY = new Map(GOTO_NAV.map((g) => [g.key, g] as const));

/** Returns true when the event target is a text-entry surface. */
function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    el.isContentEditable === true
  );
}

/** A modal/dialog/palette is currently open (so global chords should defer). */
function overlayOpen(): boolean {
  return document.querySelector('[aria-modal="true"]') != null;
}

export interface GlobalShortcutHandlers {
  /** Open the `?` cheat-sheet overlay. */
  onHelp: () => void;
  /** Open the command palette (mapped to `/`). */
  onOpenPalette: () => void;
}

/**
 * Registers the global shortcut listener for the lifetime of the shell.
 * Handlers are read through a ref so the listener stays stable.
 */
export function useGlobalShortcuts({ onHelp, onOpenPalette }: GlobalShortcutHandlers): void {
  const navigate = useNavigate();
  const handlers = useRef({ onHelp, onOpenPalette });
  handlers.current = { onHelp, onOpenPalette };
  // Timestamp of the last standalone `g` press (0 = no pending chord).
  const pendingG = useRef(0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isEditableTarget(e.target)) return;
      if (overlayOpen()) return;

      const key = e.key;

      // WCAG 2.1.4 (Character Key Shortcuts): the single-character `/` and `?`
      // bindings below are safe because they are (a) suppressed while any
      // editable field is focused (isEditableTarget above), (b) never fire with
      // a modifier held, and (c) deferred while a modal/palette is open. They
      // are also surfaced in the `?` cheat-sheet and advertised via
      // `aria-keyshortcuts` on their header triggers, so they are discoverable.

      // `?` (Shift + / on most layouts) → cheat-sheet.
      if (key === '?') {
        e.preventDefault();
        pendingG.current = 0;
        handlers.current.onHelp();
        return;
      }

      // `/` → open the command palette as a global search entry point.
      if (key === '/') {
        e.preventDefault();
        pendingG.current = 0;
        handlers.current.onOpenPalette();
        return;
      }

      const lower = key.toLowerCase();

      // Second key of a `g …` chord.
      if (pendingG.current && Date.now() - pendingG.current < 1200) {
        pendingG.current = 0;
        const target = GOTO_BY_KEY.get(lower);
        if (target) {
          e.preventDefault();
          void navigate(target.to);
        }
        return;
      }

      // Start a `g …` chord.
      if (lower === 'g') {
        pendingG.current = Date.now();
        return;
      }

      pendingG.current = 0;
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);
}

// ---------------------------------------------------------------------------
// Cheat-sheet overlay
// ---------------------------------------------------------------------------

function Keys({ keys }: { keys: string[] }) {
  return (
    <span className="flex shrink-0 items-center gap-1">
      {keys.map((k, i) => (
        <kbd
          key={`${k}-${i}`}
          className="min-w-[1.5rem] rounded border border-border bg-surface-2 px-1.5 py-0.5 text-center text-[11px] font-medium text-foreground"
        >
          {k}
        </kbd>
      ))}
    </span>
  );
}

function Row({ keys, label }: { keys: string[]; label: ReactNode }) {
  return (
    <li className="flex items-center justify-between gap-3 rounded-token px-2 py-1.5 text-sm text-foreground">
      <span className="min-w-0 truncate">{label}</span>
      <Keys keys={keys} />
    </li>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <h3 className="mb-1.5 px-2 text-[11px] font-semibold uppercase tracking-wide text-muted">
        {title}
      </h3>
      <ul className="space-y-0.5">{children}</ul>
    </div>
  );
}

const MOD_KEY = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
  ? '⌘'
  : 'Ctrl';

export interface ShortcutsOverlayProps {
  open: boolean;
  onClose: () => void;
}

/** The `?` cheat-sheet listing every global shortcut, grouped. */
export function ShortcutsOverlay({ open, onClose }: ShortcutsOverlayProps) {
  const { t } = useT();
  if (!open) return null;
  return (
    <Modal title={t('shell.app.keyboardShortcuts.keyboardShortcuts')} size="lg" onClose={onClose}>
      <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
        <Section title={t('shell.app.keyboardShortcuts.general')}>
          <Row keys={[MOD_KEY, 'K']} label={t('shell.app.keyboardShortcuts.commandPalette')} />
          <Row keys={['/']} label={t('shell.app.keyboardShortcuts.search')} />
          <Row keys={['?']} label={t('shell.app.keyboardShortcuts.showThisHelp')} />
          <Row keys={['Esc']} label={t('shell.app.keyboardShortcuts.closeGoBack')} />
        </Section>

        <Section title={t('shell.app.keyboardShortcuts.navigationGThen')}>
          {GOTO_NAV.slice(0, Math.ceil(GOTO_NAV.length / 2)).map((g) => (
            <Row key={g.key} keys={['g', g.key]} label={g.label} />
          ))}
        </Section>

        <Section title={t('shell.app.keyboardShortcuts.navigationContinued')}>
          {GOTO_NAV.slice(Math.ceil(GOTO_NAV.length / 2)).map((g) => (
            <Row key={g.key} keys={['g', g.key]} label={g.label} />
          ))}
        </Section>

        <Section title={t('shell.app.keyboardShortcuts.inThePalette')}>
          <Row keys={['↑', '↓']} label={t('shell.app.keyboardShortcuts.browseTheResults')} />
          <Row keys={['↵']} label={t('shell.app.keyboardShortcuts.openTheSelection')} />
        </Section>
      </div>
      <p className="mt-5 border-t border-border pt-3 text-xs text-muted">{t('shell.app.keyboardShortcuts.tipPress')}{' '}<kbd className="rounded border border-border bg-surface-2 px-1 text-[10px]">g</kbd>{' '}{t('shell.app.keyboardShortcuts.thenALetterToSwitchSection')}</p>
    </Modal>
  );
}
